import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { formatDate } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	ElementRef,
	LOCALE_ID,
	TemplateRef,
	ViewContainerRef,
	computed,
	effect,
	inject,
	input,
	model,
	output,
	signal,
	untracked,
	viewChild
} from '@angular/core';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';
import { TwoLetterDayNamesDateAdapter, XpDateRangeHeaderComponent } from '../date-range/xp-date-range.component';

const REPEAT_DELAY_MS = 500;
const REPEAT_INTERVAL_MS = 100;

type TimePart = 'hour' | 'minute';

@Component({
	selector: 'xp-date-time',
	standalone: true,
	imports: [MatDatepickerModule, MatIconModule],
	providers: [provideNativeDateAdapter(), { provide: DateAdapter, useClass: TwoLetterDayNamesDateAdapter }],
	templateUrl: './xp-date-time.component.html',
	styleUrls: ['./xp-date-time.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpDateTimeComponent {
	value = model<Date | null>(null);
	max = input<Date | null>(null);
	disabled = input(false);
	ariaLabel = input<string | null>(null);
	closed = output<Date | null>();

	readonly header = XpDateRangeHeaderComponent;
	readonly open = signal(false);
	readonly text = computed(() => {
		const value = this.value();
		return value ? formatDate(value, 'MM/dd/y hh:mm a', this.locale) : '';
	});
	readonly hourText = computed(() => pad(this.shown().getHours() % 12 || 12));
	readonly minuteText = computed(() => pad(this.shown().getMinutes()));
	readonly meridiem = computed(() => (this.shown().getHours() < 12 ? 'AM' : 'PM'));

	/* The spinner shows the time while a step button is held; the value changes once, on release, as in p-calendar. */
	private readonly draft = signal<Date | null>(null);
	private readonly shown = computed(() => this.draft() ?? this.value() ?? new Date());

	private readonly overlay = inject(Overlay);
	private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly locale = inject(LOCALE_ID);
	private readonly fieldBox = viewChild.required<ElementRef<HTMLElement>>('fieldBox');
	private readonly panelTemplate = viewChild.required<TemplateRef<unknown>>('panelTemplate');
	private readonly panelOutlet = viewChild.required('panelOutlet', { read: ViewContainerRef });
	private readonly calendar = viewChild<MatCalendar<Date>>('calendar');

	private overlayRef?: OverlayRef;
	private readonly overlayEvents = new Subscription();
	private repeatTimer?: ReturnType<typeof setTimeout>;
	/* A value this component set itself must not move the month, also when the screen writes back a copy: the user is looking at it. */
	private ownValue: Date | null = null;
	private valueAtOpen: Date | null = null;

	constructor() {
		effect(() => {
			const value = this.value();
			const calendar = this.calendar();
			untracked(() => {
				if (calendar && value && value.getTime() !== this.ownValue?.getTime()) {
					calendar.activeDate = value;
				}
			});
		});

		inject(DestroyRef).onDestroy(() => {
			clearTimeout(this.repeatTimer);
			this.overlayEvents.unsubscribe();
			this.overlayRef?.dispose();
		});
	}

	openPanel(): void {
		if (this.disabled() || this.overlayRef?.hasAttached()) {
			return;
		}
		this.overlayRef ??= this.createOverlay();
		this.overlayRef.attach(new TemplatePortal(this.panelTemplate(), this.panelOutlet()));
		this.valueAtOpen = this.value();
		this.open.set(true);
	}

	togglePanel(): void {
		if (this.overlayRef?.hasAttached()) {
			this.closePanel();
			return;
		}
		this.openPanel();
	}

	closePanel(): void {
		this.endStep();
		if (!this.overlayRef?.hasAttached()) {
			return;
		}
		this.overlayRef.detach();
		this.open.set(false);
		const value = this.value();
		if (value?.getTime() !== this.valueAtOpen?.getTime()) {
			this.closed.emit(value);
		}
	}

	onDateSelected(date: Date | null): void {
		if (!date || this.disabled()) {
			return;
		}
		const next = new Date(this.value() ?? new Date());
		next.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
		this.commit(next);
	}

	startStep(part: TimePart, direction: 1 | -1, event: Event): void {
		event.preventDefault();
		if (this.disabled()) {
			return;
		}
		clearTimeout(this.repeatTimer);
		this.step(part, direction);
		this.repeatStep(part, direction, REPEAT_DELAY_MS);
	}

	stepByKey(part: TimePart, direction: 1 | -1, event: Event): void {
		event.preventDefault();
		if (!this.disabled()) {
			this.step(part, direction);
		}
	}

	endStep(): void {
		clearTimeout(this.repeatTimer);
		this.repeatTimer = undefined;
		const draft = this.draft();
		if (draft) {
			this.draft.set(null);
			this.commit(draft);
		}
	}

	toggleMeridiem(): void {
		if (this.disabled()) {
			return;
		}
		const next = new Date(this.value() ?? new Date());
		next.setHours((next.getHours() + 12) % 24);
		this.commit(next);
	}

	private repeatStep(part: TimePart, direction: 1 | -1, delay: number): void {
		this.repeatTimer = setTimeout(() => {
			this.step(part, direction);
			this.repeatStep(part, direction, REPEAT_INTERVAL_MS);
		}, delay);
	}

	private step(part: TimePart, direction: 1 | -1): void {
		const next = new Date(this.shown());
		if (part === 'hour') {
			next.setHours((next.getHours() + direction + 24) % 24);
		} else {
			next.setMinutes((next.getMinutes() + direction + 60) % 60);
		}
		this.draft.set(this.limit(next));
	}

	private commit(date: Date): void {
		const next = this.limit(date);
		this.ownValue = next;
		this.value.set(next);
	}

	private limit(date: Date): Date {
		const max = this.max();
		return max && date > max ? new Date(max) : date;
	}

	private createOverlay(): OverlayRef {
		const overlayRef = this.overlay.create({
			positionStrategy: this.overlay.position()
				.flexibleConnectedTo(this.fieldBox())
				.withFlexibleDimensions(false)
				.withPush(true)
				.withPositions([
					{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
					{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
					{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
					{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' }
				]),
			scrollStrategy: this.overlay.scrollStrategies.reposition()
		});
		this.overlayEvents.add(overlayRef.outsidePointerEvents().subscribe(event => {
			if (!this.host.nativeElement.contains(event.target as Node)) {
				this.closePanel();
			}
		}));
		this.overlayEvents.add(overlayRef.keydownEvents().subscribe(event => {
			if (event.key === 'Escape') {
				event.preventDefault();
				this.closePanel();
			}
		}));
		return overlayRef;
	}
}

function pad(value: number): string {
	return String(value).padStart(2, '0');
}
